import { API_BASE_URL, SSE_EVENTS } from '../config';
import type { CompositionProps, OutputSettings, RenderProgress } from '../types';

export interface StartRenderResult {
  jobId: string;
}

/**
 * POSTs to /api/render and returns the job ID.
 */
export async function startRender(
  compositionProps: CompositionProps,
  outputSettings: OutputSettings
): Promise<StartRenderResult> {
  const res = await fetch(`${API_BASE_URL}/api/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      templateId: compositionProps.templateId,
      compositionProps,
      outputSettings,
    }),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Render request failed (${res.status})`);
  }

  return res.json() as Promise<StartRenderResult>;
}

/**
 * Opens a Server-Sent Events connection for render progress.
 * Calls onProgress on each event, onComplete when done, onError on failure.
 * Returns a cleanup function to close the connection.
 */
export function subscribeToProgress(
  jobId: string,
  onProgress: (progress: RenderProgress) => void,
  onComplete: (outputUrl: string) => void,
  onError: (message: string) => void
): () => void {
  const es = new EventSource(`${API_BASE_URL}/api/render/${jobId}/progress`);

  es.onmessage = (e: MessageEvent<string>) => {
    try {
      const progress = JSON.parse(e.data) as RenderProgress;
      onProgress(progress);

      if (progress.stage === 'completed') {
        onComplete(progress.outputUrl ?? `/api/download/${jobId}`);
        es.close();
      } else if (progress.stage === 'error') {
        onError(progress.error ?? 'Unknown render error');
        es.close();
      }
    } catch {
      // Malformed event — ignore
    }
  };

  es.onerror = () => {
    onError('Lost connection to render server');
    es.close();
  };

  return () => es.close();
}

/**
 * Triggers a browser download of the rendered MP4.
 */
export function downloadOutput(jobId: string, filename = 'render.mp4'): void {
  const a = document.createElement('a');
  a.href = `${API_BASE_URL}/api/download/${jobId}`;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
